/**
 * Taxonomy Engine — generic, field-agnostic selection machinery.
 *
 * The engine knows nothing about restaurants or glassmorphism. It reads:
 *   - an ordered field list (the designer's decision chain)
 *   - a registry of items per field
 *   - two data properties on items: compatibleWith + requiresCapabilities
 * and derives everything: option filtering, dependency gating, and cascade
 * resets when an upstream selection changes. No if/else chains — adding a
 * field or a compatibility rule is a data change only.
 */

import { getDocumentKnowledgeByName } from '../documentKnowledge';

/**
 * Create an engine over { fields, registries }.
 * fields   = [{ id, label, requires?: [fieldIds] }] in chain order.
 *            The special field id 'documentType' holds a document display
 *            name (Phase 1 convention); all other fields hold item ids.
 * registries = { fieldId: items[] } for every field except documentType.
 */
export const createTaxonomyEngine = ({ fields, registries }) => {
  const fieldOrder = fields.map((f) => f.id);
  const fieldById = new Map(fields.map((f) => [f.id, f]));

  const knowledgeFor = (selections) =>
    selections.documentType ? getDocumentKnowledgeByName(selections.documentType) : null;

  const isCompatible = (item, selections) => {
    // Every compatibleWith entry must accept the current upstream selection
    // (unselected upstream = no constraint yet).
    const compatOk = Object.entries(item.compatibleWith || {}).every(([fieldId, allowed]) => {
      const selected = selections[fieldId];
      return !selected || allowed.includes(selected);
    });
    if (!compatOk) return false;

    const knowledge = knowledgeFor(selections);
    return (item.requiresCapabilities || []).every((cap) => !knowledge || knowledge[cap] === true);
  };

  /** Filtered options for a field under the current selections. */
  const getOptions = (fieldId, selections = {}) => {
    const field = fieldById.get(fieldId);
    if (!field) throw new Error(`Unknown taxonomy field "${fieldId}"`);
    // Dependency gate: no options until every required upstream is chosen.
    if ((field.requires || []).some((dep) => !selections[dep])) return [];
    return (registries[fieldId] || []).filter((item) => isCompatible(item, selections));
  };

  const getItem = (fieldId, itemId) =>
    (registries[fieldId] || []).find((item) => item.id === itemId) || null;

  const createEmptySelections = () =>
    Object.fromEntries(fieldOrder.map((id) => [id, null]));

  /**
   * Set one field and cascade: every downstream field whose current value is
   * no longer among its filtered options is reset to null. This single rule
   * implements "changing Industry resets Category" and "changing Document
   * resets incompatible selections".
   */
  const applySelection = (selections, fieldId, value) => {
    if (!fieldById.has(fieldId)) throw new Error(`Unknown taxonomy field "${fieldId}"`);
    const next = { ...selections, [fieldId]: value };

    fieldOrder
      .slice(fieldOrder.indexOf(fieldId) + 1)
      .forEach((downstreamId) => {
        const current = next[downstreamId];
        if (current === null || current === undefined) return;
        const stillValid = getOptions(downstreamId, next).some((item) => item.id === current);
        if (!stillValid) next[downstreamId] = null;
      });

    return next;
  };

  return { fields, fieldOrder, getOptions, getItem, createEmptySelections, applySelection };
};
